import { Button, ButtonProps } from "@introist/react-foundation/v2";
import styled from "styled-components";

// Calculate luminance to determine if a color is light or dark
const getLuminance = (hexColor: string): number => {
  // Remove # if present
  const hex = hexColor.replace("#", "");

  // Convert to RGB
  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  // Apply gamma correction
  const rLinear = r <= 0.03928 ? r / 12.92 : Math.pow((r + 0.055) / 1.055, 2.4);
  const gLinear = g <= 0.03928 ? g / 12.92 : Math.pow((g + 0.055) / 1.055, 2.4);
  const bLinear = b <= 0.03928 ? b / 12.92 : Math.pow((b + 0.055) / 1.055, 2.4);

  // Calculate relative luminance using WCAG formula
  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

// Determine text color based on background luminance
const getTextColorForBackground = (hexColor: string): string => {
  const luminance = getLuminance(hexColor);
  // Use WCAG contrast threshold
  return luminance > 0.5 ? "#000000" : "#FFFFFF";
};

// Darken a color by a percentage (for hover/active states)
const darkenColor = (hexColor: string, percent: number): string => {
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const darken = (value: number) => Math.max(0, Math.floor(value * (1 - percent / 100)));

  const newR = darken(r).toString(16).padStart(2, "0");
  const newG = darken(g).toString(16).padStart(2, "0");
  const newB = darken(b).toString(16).padStart(2, "0");

  return `#${newR}${newG}${newB}`;
};

// Lighten a color by a percentage (for hover/active states on dark backgrounds)
const lightenColor = (hexColor: string, percent: number): string => {
  const hex = hexColor.replace("#", "");
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const lighten = (value: number) =>
    Math.min(255, Math.floor(value + (255 - value) * (percent / 100)));

  const newR = lighten(r).toString(16).padStart(2, "0");
  const newG = lighten(g).toString(16).padStart(2, "0");
  const newB = lighten(b).toString(16).padStart(2, "0");

  return `#${newR}${newG}${newB}`;
};

// Get hover color based on background luminance
const getHoverColor = (hexColor: string): string => {
  const luminance = getLuminance(hexColor);
  // Darken light colors, lighten dark colors
  return luminance > 0.5 ? darkenColor(hexColor, 10) : lightenColor(hexColor, 15);
};

// Get active color based on background luminance
const getActiveColor = (hexColor: string): string => {
  const luminance = getLuminance(hexColor);
  // Darken light colors more, lighten dark colors more
  return luminance > 0.5 ? darkenColor(hexColor, 20) : lightenColor(hexColor, 10);
};

interface StyledButtonProps {
  $customColor?: string;
}

const StyledButton = styled(Button)<StyledButtonProps>`
  ${props =>
    props.$customColor &&
    `
    && {
      background-color: ${props.$customColor} !important;
      color: ${getTextColorForBackground(props.$customColor)} !important;
      border: none !important;
    }

    /* Target all text and icon elements */
    &&, && *, && span, && div {
      color: ${getTextColorForBackground(props.$customColor)} !important;
    }

    /* Ensure icon color matches text color */
    && svg, && svg * {
      color: ${getTextColorForBackground(props.$customColor)} !important;
      stroke: ${getTextColorForBackground(props.$customColor)} !important;
    }

    /* Hover state */
    &&:hover:not(:disabled) {
      background-color: ${getHoverColor(props.$customColor)} !important;
      color: ${getTextColorForBackground(getHoverColor(props.$customColor))} !important;
    }

    &&:hover:not(:disabled), &&:hover:not(:disabled) *, &&:hover:not(:disabled) span, &&:hover:not(:disabled) div {
      color: ${getTextColorForBackground(getHoverColor(props.$customColor))} !important;
    }

    &&:hover:not(:disabled) svg, &&:hover:not(:disabled) svg * {
      color: ${getTextColorForBackground(getHoverColor(props.$customColor))} !important;
      stroke: ${getTextColorForBackground(getHoverColor(props.$customColor))} !important;
    }

    /* Active state */
    &&:active:not(:disabled) {
      background-color: ${getActiveColor(props.$customColor)} !important;
      color: ${getTextColorForBackground(getActiveColor(props.$customColor))} !important;
      transform: scale(0.98);
    }

    &&:active:not(:disabled), &&:active:not(:disabled) *, &&:active:not(:disabled) span, &&:active:not(:disabled) div {
      color: ${getTextColorForBackground(getActiveColor(props.$customColor))} !important;
    }

    &&:active:not(:disabled) svg, &&:active:not(:disabled) svg * {
      color: ${getTextColorForBackground(getActiveColor(props.$customColor))} !important;
      stroke: ${getTextColorForBackground(getActiveColor(props.$customColor))} !important;
    }

    /* Focus state */
    &&:focus-visible {
      outline: 2px solid ${props.$customColor};
      outline-offset: 2px;
    }
  `}
`;

export interface CustomColorButtonProps extends Omit<ButtonProps, "variant"> {
  customColor?: string;
}

export const CustomColorButton = ({ customColor, ...props }: CustomColorButtonProps) => {
  return <StyledButton {...props} $customColor={customColor} variant="filled" />;
};
